import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject, catchError, debounceTime, of, map, takeUntil } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PersonAgeStatus } from '@core/api/generated/api-client';
import { personAgeStatusLabels, personAgeStatusOptions } from '@core/api/enums/person-age-status.labels';
import {
  MemberFollowUpApiService,
  MemberFollowUpItem,
} from '@core/api/services/member-follow-up-api.service';
import { FilterChip, PaginationConfig, TableColumn } from '@shared/components/data-table/data-table.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { ToolbarSortSelection } from '@shared/components/table-toolbar/table-toolbar.component';
import { AgeStatusMultiSelectFilterComponent, ageStatusFilterOptions, UNDETERMINED_AGE_FILTER } from '@shared/components/age-status-multi-select-filter/age-status-multi-select-filter.component';
import { MemberReferenceComponent } from '@shared/components/member-reference/member-reference.component';
import { UnitService } from '@features/units/services/unit.service';
import { PersonMultiSelectFilterComponent, PersonFilterOption, PersonFilterSelection } from '@shared/components/person-multi-select-filter/person-multi-select-filter.component';
import { BreadcrumbService } from '@core/services/breadcrumb.service';

@Component({
  selector: 'app-member-follow-up-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterLink,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    TableSurfaceComponent,
    AgeStatusMultiSelectFilterComponent,
    MemberReferenceComponent,
    PersonMultiSelectFilterComponent,
  ],
  templateUrl: './member-follow-up-page.component.html',
  styleUrl: './member-follow-up-page.component.scss',
})
export class MemberFollowUpPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly search$ = new Subject<string>();
  private loadVersion = 0;

  readonly homeAttendanceOptions = [
    { value: 'true', label: 'Atendidos en el hogar' },
    { value: 'false', label: 'No atendidos en el hogar' },
  ];
  readonly filterForm = new FormGroup({
    ageStatuses: new FormControl<string[]>(ageStatusFilterOptions.map(option => option.value), { nonNullable: true }),
    responsiblePriestIds: new FormControl<PersonFilterSelection>(null),
    isHomeAttended: new FormControl<string>('all', { nonNullable: true }),
  });

  readonly columns: TableColumn[] = [
    { key: 'fullName', header: 'Miembro', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'communityName', header: 'Comunidad', hideable: true, visible: true, resizable: true },
    { key: 'age', header: 'Edad', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'responsiblePriest', header: 'Pastor de hogar', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'isHomeAttended', header: 'Atendido en el hogar', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'lastAttendance', header: 'Última concurrencia', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'lastSpiritualAssistanceVisit', header: 'Última VAE', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'lastHolyCommunion', header: 'Última Santa Cena', sortable: true, hideable: true, visible: true, resizable: true },
  ];

  items: MemberFollowUpItem[] = [];
  responsiblePriests: PersonFilterOption[] = [];
  activeFilterChips: FilterChip[] = [];
  isLoading = true;
  page = 1;
  pageSize = 25;
  totalCount = 0;
  totalPages = 0;
  unitName = '';
  currentSort: ToolbarSortSelection = { sortBy: 'lastAttendance', direction: 'asc' };
  readonly unitId: string;

  constructor(
    private readonly api: MemberFollowUpApiService,
    private readonly unitService: UnitService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly cdr: ChangeDetectorRef,
    route: ActivatedRoute,
  ) {
    this.unitId = route.snapshot.parent?.paramMap.get('unit-id') ?? '';
    this.applyFocus(route.snapshot.queryParamMap.get('focus'));
  }

  readonly searchResponsiblePriests = (term: string, page: number) => this.api.getResponsiblePriests(this.unitId, term, page).pipe(
    map(response => ({
      ...response,
      items: response.items.map(person => ({
        id: person.id,
        fullName: person.fullName,
        description: person.ministryName + (person.ministryActivityUnitNames.length ? ' en ' + person.ministryActivityUnitNames.join(', ') : ''),
      })),
    })),
  );

  onResponsiblePriestSelectionChange(people: PersonFilterOption[]): void {
    this.responsiblePriests = people;
    this.updateFilterChips();
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    if (!this.unitId) {
      this.isLoading = false;
      return;
    }

    this.unitService.getUnit(this.unitId)
      .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(unit => {
        this.unitName = unit?.name ?? 'Unidad';
        this.breadcrumbService.setDynamicCrumb('unit-id', this.unitName);
        this.cdr.markForCheck();
      });

    this.filterForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.page = 1;
      this.load();
    });
    this.search$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.page = 1;
      this.load();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get paginationConfig(): PaginationConfig {
    return {
      currentPage: this.page,
      pageSize: this.pageSize,
      totalCount: this.totalCount,
      totalPages: this.totalPages,
    };
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.search$.next(term);
  }

  onSearchInput(): void {
    this.isLoading = true;
    this.cdr.markForCheck();
  }

  onSortChange(sort: ToolbarSortSelection): void {
    this.currentSort = sort;
    this.page = 1;
    this.load();
  }

  onPageChange(event: { page: number }): void {
    this.page = event.page;
    this.load();
  }

  onPageSizeChange(event: { pageSize: number }): void {
    this.pageSize = event.pageSize;
    this.page = 1;
    this.load();
  }

  onFilterRemove(key: string): void {
    switch (key) {
      case 'ageStatuses': this.filterForm.controls.ageStatuses.setValue(ageStatusFilterOptions.map(option => option.value)); break;
      case 'responsiblePriestIds': this.filterForm.controls.responsiblePriestIds.setValue(null); break;
      case 'isHomeAttended': this.filterForm.controls.isHomeAttended.setValue('all'); break;
    }
  }

  formatDate(value?: string | null): string {
    if (!value) return '-';
    const date = value.includes('T') ? new Date(value) : new Date(`${value}T00:00:00`);
    return new Intl.DateTimeFormat('es-AR').format(date);
  }

  getDaysSince(value?: string | null): number | null {
    if (!value) return null;

    const date = value.includes('T') ? new Date(value) : new Date(`${value}T00:00:00`);
    const today = new Date();
    const dateAtMidnight = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const todayAtMidnight = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.max(0, Math.floor((todayAtMidnight - dateAtMidnight) / 86_400_000));
  }

  isHolyCommunionOverdue(value?: string | null): boolean {
    const daysSince = this.getDaysSince(value);
    return daysSince !== null && daysSince > 14;
  }

  getAgeStatusLabel(value?: PersonAgeStatus | null): string {
    return value ? personAgeStatusLabels[value] ?? value : '';
  }

  private load(): void {
    const version = ++this.loadVersion;
    this.isLoading = true;
    const filters = this.filterForm.getRawValue();
    this.api.get(this.unitId, {
      page: this.page,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm,
      ageStatuses: filters.ageStatuses.filter(value => value !== UNDETERMINED_AGE_FILTER) as PersonAgeStatus[],
      includeUndeterminedAge: filters.ageStatuses.includes(UNDETERMINED_AGE_FILTER),
      noAgeStatusesSelected: filters.ageStatuses.length === 0,
      responsiblePriestIds: filters.responsiblePriestIds ?? [],
      noResponsiblePriestsSelected: filters.responsiblePriestIds !== null && filters.responsiblePriestIds.length === 0,
      isHomeAttended: filters.isHomeAttended === 'all' ? null : filters.isHomeAttended === 'true',
      sortBy: this.currentSort.sortBy,
      sortDirection: this.getApiSortDirection(),
    }).pipe(
      catchError(() => of({ items: [], totalCount: 0, pageNumber: this.page, pageSize: this.pageSize, totalPages: 0 })),
      takeUntil(this.destroy$),
    ).subscribe(response => {
      if (version !== this.loadVersion) return;
      this.items = response.items;
      this.totalCount = response.totalCount;
      this.totalPages = response.totalPages;
      this.page = response.pageNumber;
      this.pageSize = response.pageSize;
      this.isLoading = false;
      this.updateFilterChips();
      this.cdr.markForCheck();
    });
  }

  private searchTerm = '';

  private applyFocus(focus: string | null): void {
    switch (focus) {
      case 'visits':
        this.currentSort = { sortBy: 'lastSpiritualAssistanceVisit', direction: 'desc' };
        break;
      case 'attendance':
        this.currentSort = { sortBy: 'lastAttendance', direction: 'desc' };
        break;
      case 'communion':
        this.currentSort = { sortBy: 'lastHolyCommunion', direction: 'desc' };
        this.filterForm.controls.isHomeAttended.setValue('true', { emitEvent: false });
        break;
    }
  }

  private getApiSortDirection(): 'asc' | 'desc' {
    const dateColumns = ['lastAttendance', 'lastSpiritualAssistanceVisit', 'lastHolyCommunion'];
    if (!dateColumns.includes(this.currentSort.sortBy)) return this.currentSort.direction;
    return this.currentSort.direction === 'asc' ? 'desc' : 'asc';
  }

  private updateFilterChips(): void {
    const filters = this.filterForm.getRawValue();
    const chips: FilterChip[] = [];
    if (filters.ageStatuses.length !== ageStatusFilterOptions.length) chips.push({ key: 'ageStatuses', label: 'Edad', value: `${filters.ageStatuses.length} seleccionado(s)` });
    if (filters.responsiblePriestIds !== null) {
      const selectedPastors = filters.responsiblePriestIds
        .map(id => this.responsiblePriests.find(priest => priest.id === id)?.fullName ?? id)
        .join(', ');
      chips.push({ key: 'responsiblePriestIds', label: 'Pastor de hogar', value: selectedPastors || 'Ninguno' });
    }
    if (filters.isHomeAttended !== 'all') chips.push({ key: 'isHomeAttended', label: 'Atención en el hogar', value: filters.isHomeAttended === 'true' ? 'Atendidos' : 'No atendidos' });
    this.activeFilterChips = chips;
  }

}
